import { useCallback, useEffect, useState } from 'react';
import { authAPI } from '../../services/api';
import useSocketReconnect from '../../hooks/useSocketReconnect';
import useChatSocket from './useChatSocket';

// 「某條對話剛被標記已讀」的自家訊號。用 window 事件而非 context：
// 發出端（對話室）與接收端（底部導航列、首頁）在元件樹上沒有共同祖先，
// 為了一個整數拉一層 provider 不划算。
export const CHAT_READ_EVENT = 'chat:read';
export const notifyChatRead = () => window.dispatchEvent(new Event(CHAT_READ_EVENT));

// 教職員的「有新訊息的對話串數」。首頁統計卡與底部導航列紅點共用同一份資料。
//
// 【為什麼是串數不是人頭 —— 2026-08-19 user 拍板推翻同日稍早的去重】
// 分串（一位家長一條）當天先做成「同一個孩子的爸媽只算一個家庭」，user 當天稍晚推翻：
// 「只要有一串對話有新訊息就算一個，一個學生 爸爸和媽媽都傳訊息給老師 就是兩個訊息」。
// 理由是爸媽各傳一則對老師是**兩件要回的事**——去重會讓他看到 1、回完就以為清空了。
//
// 【但仍不是則數】同一條串連傳 5 則只算 1（user 2026-08-13 定調，這一層沒被推翻）：
// 老師點進去那一次就一起處理完了。
// 實際計算在後端 /chat/unread-summary（前端不自己加總，避免兩處語意漂移）。
//
// 【為什麼綁 WebSocket，還要另一個事件 —— 2026-08-13 修 user 回報】
// WS 只在「有新訊息」時發，**老師自己點開對話把它讀掉並不會發 WS**
// （那只是打 POST /chat/{id}/read 更新已讀水位）。所以只綁 WS 的話，
// 紅點在點開訊息後不會消失，要等下一則訊息進來才更新
// —— user 回報「紅點不會因為我點開訊息而消失，要即時反應」。
// 因此另外收一個自家事件 CHAT_READ_EVENT，由呼叫 read API 的地方發出。
// 量體只有一個整數，重抓比在前端逐筆維護簡單可靠。
export default function useUnreadSummary() {
    const [count, setCount] = useState(0);

    const refresh = useCallback(() => {
        authAPI.chat.unreadSummary()
            // 只讀新欄位。後端過渡期仍回傳同值的舊名 students_with_unread，但那是留給
            // 還沒更新到的舊 bundle 的，這裡再 fallback 過去只會讓舊名更難刪掉。
            .then((r) => setCount(r.data?.threads_with_unread ?? 0))
            // 靜默失敗：這是裝飾性的紅點，不該因為一次網路波動就跳錯誤打斷操作
            .catch(() => {});
    }, []);

    useEffect(() => { refresh(); }, [refresh]);
    // 第二個 handler（訊息被收回）也重抓：後端的未讀計算**排除已收回的訊息**
    //（chat_service.unread_count 有 is_recalled.is_(False) 這個條件），所以家長把唯一一則
    // 未讀訊息收回時，紅點必須跟著消失。不接的話那個紅點會一直掛著，點進去卻只有墓碑，
    // 變成怎麼點都消不掉的假未讀。
    useChatSocket(refresh, refresh);

    // WS 每次重新連上也重抓一次。這個紅點完全靠 WS 事件驅動，斷線期間到達的訊息不會補送，
    // 少了這一次重抓，紅點會停在斷線前的數字直到下一則訊息進來——而紅點是老師唯一會注意到
    // 「有人在等我回」的訊號，它沒亮＝老師不會點進去，訊息就真的被漏掉了。
    useSocketReconnect(refresh);

    useEffect(() => {
        window.addEventListener(CHAT_READ_EVENT, refresh);
        return () => window.removeEventListener(CHAT_READ_EVENT, refresh);
    }, [refresh]);

    return { count, refresh };
}
